import React from 'react'
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import styles from '../Poll.module.scss'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Bar } from 'react-chartjs-2'
import { PollText } from './PollText'

interface IComponentMap {
  [key: string]: React.ElementType
}

export type RecursiveComponentItem = {
  id: string
  type: string
  props: Record<string, any>
  children?: RecursiveComponentItem[]
}

export type RecursiveComponentCallback = (id: string, value: any) => void

export interface RecursiveComponentProps {
  data: RecursiveComponentItem[] | undefined
  responseCallback: RecursiveComponentCallback
}

const uiComponentsMap: IComponentMap = {
  view: 'view',
  text: PollText,
  image: 'img',
  text_input: TextField,
  select: Autocomplete,
  drop_down: Autocomplete,
  video: 'video',
  date_input: DatePicker,
  graph: Bar,
}

const getWebStyles = (props: any) => {
  return {
    ...props,
    // Web styles override normal styles (webStyle comes AFTER style)
    style: { ...props.style, ...props.webStyle },
    containerStyle: { ...props.containerStyle, ...props.webContainerStyle },
    inputStyle: { ...props.inputStyle, ...props.webInputStyle },
    inputWrapperStyle: { ...props.inputWrapperStyle, ...props.webInputWrapperStyle },
  }
}

export const RecursiveComponent: React.FC<RecursiveComponentProps> = ({ data, responseCallback }) => {
  if (!data) return null

  return (
    <>
      {data.map((item) => {
        const Component = uiComponentsMap[item.type]

        if (!Component) {
          console.warn('RecursiveComponent: Unknown UI type', item)
          return null
        }

        // Apply web styles if they exist
        const enhancedProps = getWebStyles(item.props)

        if (item.type === 'view') {
          return (
            <div key={item.id} style={enhancedProps.style} className={styles.viewContainer}>
              {item.children && <RecursiveComponent data={item.children} responseCallback={responseCallback} />}
            </div>
          )
        }

        if (item.type === 'text_input') {
          const { containerStyle, style, label } = enhancedProps

          return (
            <div key={item.id} style={{ width: '100%', display: 'flex', justifyContent: 'center', ...containerStyle }}>
              <TextField
                variant='outlined'
                key={item.id}
                label={label}
                {...enhancedProps}
                onChange={(e) => responseCallback(item.id, (e.target as HTMLInputElement).value)}
                InputProps={{ style }}
                autoComplete='off'
                sx={{
                  '& .MuiInputLabel-root': {
                    marginTop: '0px',
                  },
                }}
              />
            </div>
          )
        }

        if (item.type === 'image') {
          const { source, style, ...otherProps } = enhancedProps
          return (
            <div key={item.id} className={styles.imageContainer}>
              <img src={source?.uri} alt={item.id} {...otherProps} className={styles.responsiveImage} style={style} />
            </div>
          )
        }

        if (item.type === 'drop_down' || item.type === 'select') {
          const { choices, value, error, label, containerStyle, inputStyle } = enhancedProps
          return (
            <div key={item.id} style={{ width: '100%', display: 'flex', justifyContent: 'center', ...containerStyle }}>
              <FormControl fullWidth variant='outlined' error={!!error}>
                {label && <InputLabel>{label}</InputLabel>}
                <Select
                  value={value || ''}
                  onChange={(e) => {
                    responseCallback(item.id, e.target.value)
                  }}
                  label={label}
                  style={inputStyle}
                  displayEmpty
                >
                  <MenuItem value=''>
                    <em>{enhancedProps.placeholder || 'Select an option'}</em>
                  </MenuItem>
                  {choices.map((choice: string, index: number) => (
                    <MenuItem key={index} value={choice}>
                      {choice}
                    </MenuItem>
                  ))}
                </Select>
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </FormControl>
            </div>
          )
        }

        if (item.type === 'date_input') {
          const { value, containerStyle, inputStyle } = enhancedProps
          return (
            <div key={item.id} style={{ width: '100%', display: 'flex', justifyContent: 'center', ...containerStyle }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  format='YYYY-MM-DD'
                  value={value ? dayjs(value) : null}
                  onChange={(e) => responseCallback(item.id, e?.toISOString() || null)}
                  slotProps={{
                    textField: {
                      placeholder: enhancedProps.placeholder,
                      fullWidth: true,
                      InputProps: { style: inputStyle },
                      sx: {
                        '& .MuiInputBase-input': {
                          height: '0px',
                          paddingBottom: '60px',
                        },
                        '& .MuiButtonBase-root': {
                          height: '0px',
                          paddingBottom: '40px',
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          )
        }

        if (item.type === 'video') {
          const { source, ...otherProps } = enhancedProps
          return (
            <video key={item.id} controls {...otherProps}>
              <source src={source?.uri} type='video/mp4' />
              Your browser does not support the video tag.
            </video>
          )
        }

        if (item.type === "text") {
          const { text, preset, style, ...otherProps } = enhancedProps
        
          // Convert raw text into HTML-safe string (preserving line breaks, tabs, etc.)
          const formattedText = text
            ?.replace(/\n/g, "<br/>")
            ?.replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;")
        
          return (
            <PollText
              key={item.id}
              preset={preset}
              style={style}
              
            >
              {/* Render HTML tags safely */}
              <span dangerouslySetInnerHTML={{ __html: formattedText }} />
            </PollText>
          )
        }
        

        if (item.type === 'graph') {
          const { data = {}, style = {} } = enhancedProps
          const { labels = [], datasets = [] } = data

          return (
            <div key={item.id} style={{ ...style }}>
              <Bar
                data={{
                  labels,
                  datasets,
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'bottom' as const },
                    tooltip: { mode: 'index' as const, intersect: false },
                  },
                  scales: {
                    x: { stacked: true, grid: { display: false } },
                    y: { stacked: true, grid: { display: false }, max: 100 },
                  },
                }}
              />
            </div>
          )
        }

        return (
          <Component key={item.id} {...enhancedProps}>
            {item.children && <RecursiveComponent data={item.children} responseCallback={responseCallback} />}
          </Component>
        )
      })}
    </>
  )
}
